/**
 * Chart Color Management Utility
 * Provides consistent color assignment for chart datasets based on labels
 * Uses UserMap colors for first few datasets, then generates random colors
 */

// MNO color mappings for consistency across all charts
// Supports various name formats (atoma/ATOMA/Atoma) due to case-insensitive matching
export const MNO_COLORS: { [key: string]: string } = {
  'ATOMA': '#00D3F3', // Royal Blue
  'AWCC': '#A78BFA', // Navy Blue
  'ETISALAT': '#C026D3', // Purple
  'ROSHAN': '#FB923C', // Orange
  'SALAAM': '#EC4899', // Pink
};

/**
 * Get consistent color for MNO names
 * Handles case-insensitive matching and partial name matching for different environments
 * Returns hex color for known MNOs, or generates a random dark color for unknown MNOs
 * @param mnoName - The MNO name (may vary in case or contain additional text)
 * @param alpha - Optional alpha value for rgba format (0-1)
 * @returns Hex color string (e.g., '#EF4444') or rgba string if alpha is provided
 */
export const getMNOColor = (mnoName: string, alpha?: number): string => {
  if (!mnoName) {
    // Return a neutral gray color for empty names
    return alpha !== undefined ? `rgba(107, 114, 128, ${alpha})` : '#6B7280';
  }

  const normalizedInput = mnoName.toUpperCase();
  
  // First, try exact case-insensitive match
  for (const [key, color] of Object.entries(MNO_COLORS)) {
    if (normalizedInput === key.toUpperCase()) {
      return formatColor(color, alpha);
    }
  }
  
  // Then, try partial match (check if input contains key MNO names)
  for (const [key, color] of Object.entries(MNO_COLORS)) {
    const keyNormalized = key.toUpperCase();
    // Check if the input contains the key MNO name
    if (normalizedInput.includes(keyNormalized)) {
      return formatColor(color, alpha);
    }
    // Also check if key contains the input (for truncated names)
    if (keyNormalized.includes(normalizedInput) && normalizedInput.length >= 4) {
      return formatColor(color, alpha);
    }
  }
  
  // Generate consistent color based on name hash for unknown MNOs
  // This ensures the same unknown MNO always gets the same color
  const hash = hashString(mnoName);
  const r = Math.abs(hash % 128) + 100; // Range 100-227 for medium brightness
  const g = Math.abs(hash * 7 % 128) + 100;
  const b = Math.abs(hash * 13 % 128) + 100;
  
  const hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  return formatColor(hexColor, alpha);
};

/**
 * Format color with optional alpha value
 */
const formatColor = (color: string, alpha?: number): string => {
  if (alpha !== undefined) {
    // If color is already rgba, extract values
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
      }
    }
    // Convert hex to rgba
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};

/**
 * Simple hash function for consistent color generation
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Custom colors for the first 4 datasets
const CUSTOM_COLORS = [
  {
    borderColor: '#EF4444', // Red
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    pointBackgroundColor: '#EF4444',
    pointBorderColor: '#EF4444'
  },
  {
    borderColor: '#3B82F6', // Blue
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    pointBackgroundColor: '#3B82F6',
    pointBorderColor: '#3B82F6'
  },
  {
    borderColor: '#028F2A', // Green
    backgroundColor: 'rgba(2, 143, 42, 0.1)',
    pointBackgroundColor: '#028F2A',
    pointBorderColor: '#028F2A'
  },
  {
    borderColor: '#D925B2', // Pink/Magenta
    backgroundColor: 'rgba(217, 37, 178, 0.1)',
    pointBackgroundColor: '#D925B2',
    pointBorderColor: '#D925B2'
  }
];

// UserMap colors for additional datasets (starting from 5th)
const USER_MAP_COLORS = [
  {
    borderColor: '#FFC107', // Admin - Amber
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    pointBackgroundColor: '#FFC107',
    pointBorderColor: '#FFC107'
  },
  {
    borderColor: '#E91E63', // Master Distributor - Pink
    backgroundColor: 'rgba(233, 30, 99, 0.1)',
    pointBackgroundColor: '#E91E63',
    pointBorderColor: '#E91E63'
  },
  {
    borderColor: '#FF9800', // Distributor - Orange
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    pointBackgroundColor: '#FF9800',
    pointBorderColor: '#FF9800'
  },
  {
    borderColor: '#000000', // Sub Distributor - Black
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    pointBackgroundColor: '#000000',
    pointBorderColor: '#000000'
  },
  {
    borderColor: '#2196F3', // Retailer - Blue
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    pointBackgroundColor: '#2196F3',
    pointBorderColor: '#2196F3'
  },
  {
    borderColor: '#4CAF50', // Customer - Green
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    pointBackgroundColor: '#4CAF50',
    pointBorderColor: '#4CAF50'
  }
];

// Additional darker colors for more datasets
const ADDITIONAL_COLORS = [
  {
    borderColor: '#1e40af', // Dark blue
    backgroundColor: 'rgba(30, 64, 175, 0.1)',
    pointBackgroundColor: '#1e40af',
    pointBorderColor: '#1e40af'
  },
  {
    borderColor: '#dc2626', // Dark red
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
    pointBackgroundColor: '#dc2626',
    pointBorderColor: '#dc2626'
  },
  {
    borderColor: '#7c3aed', // Dark purple
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
    pointBackgroundColor: '#7c3aed',
    pointBorderColor: '#7c3aed'
  },
  {
    borderColor: '#0891b2', // Dark cyan
    backgroundColor: 'rgba(8, 145, 178, 0.1)',
    pointBackgroundColor: '#0891b2',
    pointBorderColor: '#0891b2'
  },
  {
    borderColor: '#be185d', // Dark pink
    backgroundColor: 'rgba(190, 24, 93, 0.1)',
    pointBackgroundColor: '#be185d',
    pointBorderColor: '#be185d'
  },
  {
    borderColor: '#374151', // Dark gray
    backgroundColor: 'rgba(55, 65, 81, 0.1)',
    pointBackgroundColor: '#374151',
    pointBorderColor: '#374151'
  }
];

// Function to generate a random color
const generateRandomColor = (seed: number) => {
  // Use seed to ensure consistent "random" colors
  const hue = (seed * 137.508) % 360; // Golden angle approximation
  const saturation = 70 + (seed % 30); // 70-100% saturation
  const lightness = 40 + (seed % 20); // 40-60% lightness for darker colors
  
  // Convert HSL to RGB for better compatibility
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h * 6) % 2 - 1));
  const m = l - c / 2;
  
  let r, g, b;
  if (h < 1/6) {
    r = c; g = x; b = 0;
  } else if (h < 2/6) {
    r = x; g = c; b = 0;
  } else if (h < 3/6) {
    r = 0; g = c; b = x;
  } else if (h < 4/6) {
    r = 0; g = x; b = c;
  } else if (h < 5/6) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  
  return {
    borderColor: hex,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.1)`,
    pointBackgroundColor: hex,
    pointBorderColor: hex
  };
};

// Combined color palette
export const chartColors = [...CUSTOM_COLORS, ...USER_MAP_COLORS, ...ADDITIONAL_COLORS];

/**
 * Get consistent color for a dataset label
 * Uses custom colors for first 4 datasets, then UserMap colors, then generates random colors
 * @param label - The dataset label
 * @param index - Optional index to force specific color assignment
 * @returns Color object with borderColor, backgroundColor, etc.
 */
export const getColorForLabel = (label: string, index?: number): typeof CUSTOM_COLORS[0] => {
  // If index is provided and it's within the first 4, use custom colors directly
  if (typeof index === 'number' && index < CUSTOM_COLORS.length) {
    return CUSTOM_COLORS[index];
  }
  
  // Create a simple hash from the label to ensure consistent color assignment
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    const char = label.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use custom colors for first 4 datasets, then UserMap colors, then additional colors, then random
  const totalPredefinedColors = CUSTOM_COLORS.length + USER_MAP_COLORS.length + ADDITIONAL_COLORS.length;
  const colorIndex = Math.abs(hash) % totalPredefinedColors;
  
  if (colorIndex < CUSTOM_COLORS.length) {
    // Use custom colors first
    return CUSTOM_COLORS[colorIndex];
  } else if (colorIndex < CUSTOM_COLORS.length + USER_MAP_COLORS.length) {
    // Use UserMap colors
    return USER_MAP_COLORS[colorIndex - CUSTOM_COLORS.length];
  } else if (colorIndex < totalPredefinedColors) {
    // Use additional predefined colors
    return ADDITIONAL_COLORS[colorIndex - CUSTOM_COLORS.length - USER_MAP_COLORS.length];
  } else {
    // Generate random color for additional datasets
    return generateRandomColor(Math.abs(hash));
  }
};

/**
 * Create a chart dataset with consistent colors and styling
 * @param label - The dataset label
 * @param data - The data array
 * @param options - Optional styling overrides
 * @param index - Optional index to force specific color assignment
 * @returns Chart dataset object
 */
export const createChartDataset = (
  label: string, 
  data: number[], 
  options: {
    borderWidth?: number;
    pointRadius?: number;
    pointHoverRadius?: number;
    fill?: boolean;
    tension?: number;
    pointStyle?: 'circle' | 'cross' | 'crossRot' | 'dash' | 'line' | 'rect' | 'rectRot' | 'rectRounded' | 'star' | 'triangle';
  } = {},
  index?: number
) => {
  const colors = getColorForLabel(label, index);
  const defaultOptions = {
    borderWidth: 3,
    pointRadius: 4,
    pointHoverRadius: 8,
    fill: false,
    tension: 0.1,
    pointStyle: 'circle' as const
  };
  
  return {
    label,
    data,
    ...colors,
    ...defaultOptions,
    ...options
  };
};

/**
 * Process API data into chart datasets with consistent colors
 * @param apiData - Array of objects with label and data properties
 * @param options - Optional styling overrides
 * @returns Array of chart datasets
 */
export const processApiDataForChart = (
  apiData: Array<{ label: string; data: number[] }>,
  options: Parameters<typeof createChartDataset>[2] = {}
) => {
  return apiData.map((item, index) => createChartDataset(item.label, item.data, options, index));
};

/**
 * Example usage:
 * 
 * // When you get data from API:
 * const apiData = [
 *   { label: 'Sales', data: [100, 150, 175, 150] },        // Will use custom color (#EF4444 - Red) - Index 0
 *   { label: 'Profit', data: [25, 50, 25, 75] },          // Will use custom color (#3B82F6 - Blue) - Index 1
 *   { label: 'Expenses', data: [50, 25, 75, 50] },        // Will use custom color (#028F2A - Green) - Index 2
 *   { label: 'Revenue', data: [75, 125, 100, 125] },       // Will use custom color (#D925B2 - Pink) - Index 3
 *   { label: 'Marketing', data: [30, 40, 35, 45] },       // Will use UserMap color (Admin - Amber) - Index 4
 *   { label: 'Operations', data: [20, 30, 25, 35] },      // Will use UserMap color (Master Distributor - Pink) - Index 5
 *   { label: 'Additional1', data: [10, 15, 12, 18] },     // Will use UserMap color (Distributor - Orange) - Index 6
 *   { label: 'Additional2', data: [5, 8, 6, 9] }         // Will use additional predefined color - Index 7
 * ];
 * 
 * // Create chart data:
 * const chartData = {
 *   labels: ['Jan', 'Feb', 'Mar', 'Apr'],
 *   datasets: processApiDataForChart(apiData)
 * };
 * 
 * // Or with custom styling:
 * const chartData = {
 *   labels: ['Jan', 'Feb', 'Mar', 'Apr'],
 *   datasets: processApiDataForChart(apiData, {
 *     borderWidth: 4,
 *     pointRadius: 6
 *   })
 * };
 * 
 * // Color Priority:
 * // 1. First 4 datasets: Custom colors (#EF4444, #3B82F6, #028F2A, #D925B2)
 * // 2. Next 6 datasets: UserMap colors (Admin, Master Distributor, Distributor, Sub Distributor, Retailer, Customer)
 * // 3. Next 6 datasets: Additional predefined darker colors
 * // 4. Beyond that: Randomly generated colors (consistent based on label hash)
 */
